/**
 * All-time revenue by plan tier (A7Y-2054).
 *
 * Mirrors SpendByCategoryPie (donut + labelled legend) for visual symmetry in
 * the Overview grid. Colors come from the shared `tierColor` map. With today's
 * tiny volume this is often a single ring, which still reads intentionally
 * (the center total + legend carry the meaning).
 */
import { formatCurrency } from "@/lib/utils";
import type { PnlTierAmount } from "@a7y/types";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { tierColor } from "../../palette";

/** 'pro' → 'Pro', 'other' → 'Other'. */
function tierLabel(tier: string): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

export function RevenueByTierChart({ data }: { data: PnlTierAmount[] }) {
  const rows = [...data].filter((d) => d.amount > 0).sort((a, b) => b.amount - a.amount);
  const total = rows.reduce((sum, r) => sum + r.amount, 0);

  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700">
      <h2 className="text-lg font-semibold text-zinc-900 dark:text-white mb-4">
        Revenue by tier
      </h2>
      {rows.length === 0 ? (
        <div className="flex h-[220px] items-center justify-center">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">No revenue recorded yet.</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-6 sm:flex-row">
          <div className="relative h-[200px] w-[200px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={rows}
                  dataKey="amount"
                  nameKey="tier"
                  cx="50%"
                  cy="50%"
                  innerRadius={58}
                  outerRadius={90}
                  paddingAngle={rows.length > 1 ? 2 : 0}
                  stroke="none"
                >
                  {rows.map((r) => (
                    <Cell key={r.tier} fill={tierColor(r.tier)} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#18181b",
                    border: "1px solid #3f3f46",
                    borderRadius: 8,
                    fontSize: 13,
                    color: "#f4f4f5",
                  }}
                  formatter={(value: number, name: string) => [
                    formatCurrency(value),
                    tierLabel(String(name)),
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xs text-zinc-500 dark:text-zinc-400">Total</span>
              <span className="text-lg font-semibold tabular-nums text-zinc-900 dark:text-white">
                {formatCurrency(total)}
              </span>
            </div>
          </div>
          <ul className="flex-1 space-y-2 self-stretch">
            {rows.map((r) => {
              const pct = total > 0 ? (r.amount / total) * 100 : 0;
              return (
                <li key={r.tier} className="flex items-center gap-3 text-sm">
                  <span
                    className="h-3 w-3 shrink-0 rounded-sm"
                    style={{ backgroundColor: tierColor(r.tier) }}
                  />
                  <span className="flex-1 text-zinc-700 dark:text-zinc-300">
                    {tierLabel(r.tier)}
                  </span>
                  <span className="tabular-nums font-medium text-zinc-900 dark:text-white">
                    {formatCurrency(r.amount)}
                  </span>
                  <span className="w-10 text-right tabular-nums text-xs text-zinc-400">
                    {pct.toFixed(0)}%
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
